/** Limite de produto (front) — alinhar com backend quando existir contrato. */
export const NUMBER_FIELD_DECIMAL_PLACES_MAX = 5;

export function clampNumberFieldDecimalPlaces(n: number | null | undefined): number {
  if (n == null || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(NUMBER_FIELD_DECIMAL_PLACES_MAX, Math.trunc(n)));
}

export function roundToDecimalPlaces(value: number, decimalPlaces: number): number {
  const p = clampNumberFieldDecimalPlaces(decimalPlaces);
  if (!Number.isFinite(value)) return value;
  const m = 10 ** p;
  return Math.round(value * m) / m;
}

/** Quantidade de dígitos após o separador decimal no texto guardado (pt-BR ou invariante). */
export function fractionDigitsFromStoredRaw(raw: string): number {
  const t = String(raw).trim();
  if (!t.length) return 0;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  const sep = Math.max(lastComma, lastDot);
  if (sep === -1) return 0;
  const frac = t.slice(sep + 1).replace(/\D/g, '');
  return Math.min(NUMBER_FIELD_DECIMAL_PLACES_MAX, frac.length);
}

/**
 * Casas decimais efetivas na list view: respeita o schema e complementa quando o valor tem
 * parte fracionária mas o schema veio 0 (ex.: API sem `numberDecimalPlaces`).
 */
export function effectiveListNumberDecimalPlaces(
  schemaDp: number,
  raw: string,
  n: number | null
): number {
  const s = clampNumberFieldDecimalPlaces(schemaDp);
  const fromRaw = fractionDigitsFromStoredRaw(raw);
  let fromNumber = 0;
  if (n != null && Number.isFinite(n)) {
    const r0 = roundToDecimalPlaces(n, 0);
    if (Math.abs(r0 - n) > 1e-10) {
      fromNumber = 1;
      for (let d = 1; d <= NUMBER_FIELD_DECIMAL_PLACES_MAX; d += 1) {
        if (Math.abs(roundToDecimalPlaces(n, d) - n) < 1e-10) {
          fromNumber = d;
          break;
        }
      }
    }
  }
  return clampNumberFieldDecimalPlaces(Math.max(s, fromRaw, fromNumber));
}

/**
 * Casas decimais para máscara no formulário e para serializar na API.
 * Combina schema, inferência a partir do valor e mínimo 2 quando há sufixo % (taxas).
 */
export function resolveNumberFieldDecimalPlaces(
  field:
    | { numberDecimalPlaces?: number | null; numberShowPercentSuffix?: boolean | null }
    | null
    | undefined,
  value: unknown
): number {
  const schemaDp = clampNumberFieldDecimalPlaces(field?.numberDecimalPlaces);
  const stringHint =
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : '';
  let n: number | null = null;
  if (typeof value === 'number' && Number.isFinite(value)) n = value;
  else if (typeof value === 'string' && value.trim().length > 0) {
    n = parseFlexibleNumberString(value.trim());
  }
  let dp = effectiveListNumberDecimalPlaces(schemaDp, stringHint, n);
  if (field?.numberShowPercentSuffix === true) {
    dp = Math.max(dp, 2);
  }
  return clampNumberFieldDecimalPlaces(dp);
}

/**
 * Casas decimais da máscara ao digitar (cadastro/edição): mantém o mínimo do schema e do % mesmo com
 * valor `null` após limpar o campo; reforça com inferência a partir do valor gravado.
 */
export function numberInputMaskDecimalPlaces(
  field: { numberDecimalPlaces?: number | null; numberShowPercentSuffix?: boolean | null } | null | undefined,
  committedValue: unknown
): number {
  const schemaDp = clampNumberFieldDecimalPlaces(field?.numberDecimalPlaces);
  const suffixMin = field?.numberShowPercentSuffix === true ? 2 : 0;
  const floor = Math.max(schemaDp, suffixMin);
  const resolved = resolveNumberFieldDecimalPlaces(field, committedValue);
  return clampNumberFieldDecimalPlaces(Math.max(floor, resolved));
}

/** Aceita vírgula ou ponto como separador decimal. */
export function parseFlexibleNumberString(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '');
  if (!t.length) return null;
  const normalized = t.replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Documento/código numérico pode precisar manter zeros à esquerda (ex.: CPF iniciando em 0).
 * Só preserva quando o campo é inteiro (sem casas decimais) e contém apenas dígitos.
 */
export function shouldPreserveLeadingZeroIntegerRaw(raw: string, decimalPlaces: number): boolean {
  if (clampNumberFieldDecimalPlaces(decimalPlaces) !== 0) return false;
  const normalized = String(raw).trim().replace(/\s+/g, '');
  if (!/^\d+$/.test(normalized)) return false;
  return normalized.length > 1 && normalized.startsWith('0');
}

/**
 * Interpreta texto de moeda pt-BR (ex.: "R$ 1.234,56", "1234,56", "60422.08").
 * Usado na edição inline da list view para campos CURRENCY.
 */
export function parseMoneyStringPtBr(raw: string): number | null {
  const t = raw.replace(/\u00a0/g, ' ').trim();
  if (!t.length) return null;
  let s = t.replace(/R\$\s*/gi, '').replace(/\s+/g, '');
  if (!s.length) return null;
  if (/^\d+$/.test(s)) return Number(s);
  if (s.includes(',')) {
    const parts = s.split(',');
    if (parts.length !== 2) return null;
    const intPart = parts[0].replace(/\./g, '');
    const frac = parts[1];
    if (!/^\d+$/.test(intPart) || !/^\d+$/.test(frac)) return null;
    const n = Number(`${intPart}.${frac}`);
    return Number.isFinite(n) ? n : null;
  }
  return parseFlexibleNumberString(s);
}

/** Máscara ao digitar: só dígitos, últimas `dp` posições são decimais (como centavos em moeda). */
export const NUMBER_MASK_MAX_DIGITS = 18;

export function parseDigitsAsMaskedDecimal(
  rawInput: string,
  decimalPlaces: number
): { value: number | null; digits: string } {
  const dp = clampNumberFieldDecimalPlaces(decimalPlaces);
  if (dp <= 0) return { value: null, digits: '' };
  const digits = rawInput.replace(/\D+/g, '').slice(0, NUMBER_MASK_MAX_DIGITS);
  if (!digits.length) return { value: null, digits: '' };
  const n = Number(digits) / 10 ** dp;
  if (!Number.isFinite(n)) return { value: null, digits: '' };
  return { value: roundToDecimalPlaces(n, dp), digits };
}

/**
 * Serialização enviada à API (`value` string) — cultura invariante, separador `.`.
 * Ex.: p=2 → "12.50"; p=0 → "12".
 */
export function formatNumberFieldValueForApi(value: number, decimalPlaces: number): string {
  const p = clampNumberFieldDecimalPlaces(decimalPlaces);
  const x = roundToDecimalPlaces(value, p);
  if (!Number.isFinite(x)) return '0';
  return x.toLocaleString('en-US', {
    minimumFractionDigits: p,
    maximumFractionDigits: p,
    useGrouping: false,
  });
}

export function parseStoredNumberValue(raw: unknown, decimalPlaces: number): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return roundToDecimalPlaces(raw, decimalPlaces);
  }
  if (typeof raw === 'string') {
    const n = parseFlexibleNumberString(raw);
    return n == null ? null : roundToDecimalPlaces(n, decimalPlaces);
  }
  return null;
}

export function formatNumberFieldForDisplayPtBr(
  value: number | null,
  decimalPlaces: number,
  opts?: { percentSuffix?: boolean }
): string {
  if (value == null || !Number.isFinite(value)) return '';
  const p = clampNumberFieldDecimalPlaces(decimalPlaces);
  const x = roundToDecimalPlaces(value, p);
  const s = x.toLocaleString('pt-BR', {
    minimumFractionDigits: p,
    maximumFractionDigits: p,
    useGrouping: false,
  });
  return opts?.percentSuffix ? `${s}\u00A0%` : s;
}
